import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Tent, User, Mail, Phone, ArrowRight, CheckCircle2, Lock, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePromoterStatus } from '../hooks/usePromoterStatus';
import toast from 'react-hot-toast';

export default function SubmitFestivalPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const promoter = usePromoterStatus();

  const [festivalName, setFestivalName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!festivalName || !contactName || !contactEmail) {
      toast.error('Nom du festival, contact et email sont requis');
      return;
    }
    setSubmitting(true);
    try {
      const token = await user.getIdToken();
      const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/promoter-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ festivalName, contactName, contactEmail, contactPhone }),
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.error || 'Erreur');
      setDone(true);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || promoter.loading) {
    return <div className="min-h-screen bg-[#0D0D0F] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF5C00]/20 border-t-[#FF5C00] rounded-full animate-spin" />
    </div>;
  }

  // Garde : codeActive requis
  if (!user || !promoter.codeActive) {
    return (
      <div className="min-h-screen bg-[#0D0D0F] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <Lock className="w-8 h-8 text-[#FF5C00] mx-auto mb-4" />
          <h1 className="text-2xl font-black mb-3">Accès réservé aux promoteurs actifs</h1>
          <p className="text-gray-400 text-sm mb-6">
            {!user
              ? 'Connectez-vous avec votre compte promoteur.'
              : promoter.hasProfile
              ? 'Votre code affilié doit être activé (contrat signé) pour soumettre un festival.'
              : 'Vous n\'êtes pas encore promoteur. Rejoignez le programme d\'affiliation.'}
          </p>
          <button
            onClick={() => navigate(promoter.hasProfile ? '/promoter/sign' : '/become-promoter')}
            className="bg-[#FF5C00] hover:bg-[#e05000] text-white px-8 py-3.5 rounded-2xl font-black text-sm transition-all"
          >
            {promoter.hasProfile ? 'Signer mon contrat' : 'Devenir promoteur'}
          </button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#0D0D0F] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-black mb-3">Lead soumis 🎉</h1>
          <p className="text-gray-400 text-sm leading-relaxed mb-8">
            Une invitation a été envoyée à l'organisateur. Dès qu'il crée son compte et génère des revenus sur Festin, votre commission de 20% est créditée automatiquement.
          </p>
          <div className="flex flex-col gap-3">
            <button onClick={() => { setDone(false); setFestivalName(''); setContactName(''); setContactEmail(''); setContactPhone(''); }}
              className="bg-[#FF5C00] hover:bg-[#e05000] text-white px-8 py-3.5 rounded-2xl font-black text-sm transition-all">
              Soumettre un autre festival
            </button>
            <button onClick={() => navigate('/promoter/dashboard')}
              className="bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-3.5 rounded-2xl font-black text-sm transition-all">
              Voir mon dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0F] text-white">
      <div className="max-w-xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 bg-[#FF5C00]/10 border border-[#FF5C00]/20 text-[#FF5C00] text-xs font-black px-3 py-1.5 rounded-full mb-5">
            <Send className="w-3.5 h-3.5" /> SOUMETTRE UN LEAD
          </div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight mb-2">Soumettre un festival</h1>
          <p className="text-gray-400 text-sm leading-relaxed">
            Vous avez convaincu un organisateur ? Renseignez ses coordonnées : on lui envoie une invitation et votre code assure le suivi de votre commission.
          </p>
        </div>

        {/* Code affilié pré-rempli non modifiable */}
        <div className="bg-[#161618] border border-[#FF5C00]/20 rounded-2xl p-4 mb-6 flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-[#FF5C00] shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Votre code affilié (automatique)</p>
            <p className="text-lg font-black text-[#FF5C00]">{promoter.affiliateCode}</p>
          </div>
          <Lock className="w-4 h-4 text-gray-600 ml-auto shrink-0" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Nom du festival *</label>
            <div className="relative">
              <Tent className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input type="text" value={festivalName} onChange={e => setFestivalName(e.target.value)} required
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF5C00]/50" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Nom du contact organisateur *</label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input type="text" value={contactName} onChange={e => setContactName(e.target.value)} required
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF5C00]/50" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Email organisateur *</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} required
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF5C00]/50" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
              Téléphone organisateur <span className="text-gray-600 normal-case font-medium">(optionnel)</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input type="tel" value={contactPhone} onChange={e => setContactPhone(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF5C00]/50" />
            </div>
          </div>

          <button type="submit" disabled={submitting}
            className="w-full flex items-center justify-center gap-3 bg-[#FF5C00] hover:bg-[#e05000] disabled:opacity-40 text-white py-4 rounded-2xl font-black text-sm transition-all">
            {submitting ? 'Envoi en cours…' : 'Envoyer l\'invitation'}
            {!submitting && <ArrowRight className="w-4 h-4" />}
          </button>
          <p className="text-center text-[11px] text-gray-600">
            L'organisateur reçoit un email d'invitation à créer son profil Festin, rattaché à votre code.
          </p>
        </form>
      </div>
    </div>
  );
}
